<script>
    import { MaterialAppMin, Container, TextField, Overlay, ProgressCircular, NavigationDrawer, Subheader, Icon, List, ListItem, ListGroup, ListItemGroup, Col, Row, Select, Switch } from "svelte-materialify/src";
    import { mdiChevronUp, mdiCog, mdiMapLegend } from "@mdi/js";
    import * as d3 from "d3";
    import { Delaunay } from "d3-delaunay";

    const names_dict = {
        resolution_too_low: "lores",
        shapes_too_thinsmall: "thin",
        issues_with_text_size_or_font: "textize",
        text_broken: "brotex",
        brightness_contrast_too_low: "locont",
        colors_not_distinguishable: "colnodi",
        colors_barely_distinguishable: "colbadi",
        continous_color_scale_ambiguos: "scambi",
        highlighted_elements_do_not_pop_out: "hinopo",
        loss_of_detailed_information_content: "infloss",
        fabricated_information: "fabinf",
        works_with_one_color: "singcol",
        has_labels: "labels",
        descriptive_text: "desc",
        texture_helps: "texture",
        colors_dont_change: "colstay",
        order_helps: "order",
    };

    let data = [];
    let data_dict = {};
    let width = 480;
    let margin = 25;

    let aggregations = [
        { name: "cvd", value: "cvd" },
        { name: "track", value: "track" },
    ];
    let aggregation = "cvd";

    let colorBy = "conference";
    let issues_names = ["resolution_too_low", "shapes_too_thinsmall", "issues_with_text_size_or_font", "text_broken", "brightness_contrast_too_low", "colors_not_distinguishable", "colors_barely_distinguishable", "continous_color_scale_ambiguos", "highlighted_elements_do_not_pop_out", "loss_of_detailed_information_content", "fabricated_information"];
    let helpful_names = ["works_with_one_color", "has_labels", "descriptive_text", "texture_helps", "colors_dont_change", "order_helps"];

    let all_names = issues_names.concat(helpful_names);

    let labels = ["InfoVis", "VIS", "VAST", "SciVis"];
    let rankings = ["Accessible", "Borderline", "Not_Accessible"];
    let showWhat = "points";
    let rankings_color_scale = {
        Accessible: d3
            .scaleLinear()
            .domain([0, 1])
            .range([d3.hcl("white"), d3.hcl("#479fb5")]),
        Borderline: d3
            .scaleLinear()
            .domain([0, 1])
            .range([d3.hcl("white"), d3.hcl("#0a5f93")]),
        Not_Accessible: d3
            .scaleLinear()
            .domain([0, 1])
            .range([d3.hcl("white"), d3.hcl("#252961")]),
    };
    let rankings_index = {
        Accessible: 0,
        Borderline: 1,
        Not_Accessible: 2,
    };
    $: label_scale = d3.scaleOrdinal(d3.schemeTableau10).domain(labels);
    $: ranking_scale = colorBy == "conference" ? null : d3.scaleSequential(rankings_color_scale[colorBy]);
    $: shape_scale = d3.scaleOrdinal(d3.symbols.map((s) => d3.symbol().size(50).type(s)())).domain(labels);

    $: init(aggregation);

    let loading = true;

    async function init(aggregation) {
        loading = true;
        const D = await d3.json(`${window.location.href}data/2021_06_data_aggregation_${aggregation}.json`, d3.autoType);
        const K = Object.keys(D);
        data = [];
        for (const condition of K) {
            D[condition].issues = {
                x: d3
                    .scaleLinear()
                    .domain(d3.extent(D[condition].projection.issues, (d) => d[0]))
                    .range([margin, width - margin]),
                y: d3
                    .scaleLinear()
                    .domain(d3.extent(D[condition].projection.issues, (d) => d[1]))
                    .range([margin, width - margin]),
            };
            D[condition].helpful = {
                x: d3
                    .scaleLinear()
                    .domain(d3.extent(D[condition].projection.helpful, (d) => d[0]))
                    .range([margin, width - margin]),
                y: d3
                    .scaleLinear()
                    .domain(d3.extent(D[condition].projection.helpful, (d) => d[1]))
                    .range([margin, width - margin]),
            };

            let delaunay_issues = Delaunay.from(D[condition].projection.issues.map(([px, py]) => [D[condition].issues.x(px), D[condition].issues.y(py)]));
            let cells_issues = delaunay_issues.voronoi([0, 0, width, width]);
            let delaunay_helpful = Delaunay.from(D[condition].projection.helpful.map(([px, py]) => [D[condition].helpful.x(px), D[condition].helpful.y(py)]));
            D[condition].cells = {
                issues: Array.from(cells_issues.cellPolygons()),
                helpful: Array.from(delaunay_helpful.voronoi([0, 0, width, width]).cellPolygons()),
            };
            D[condition].cells.issues.delaunay = delaunay_issues;
            D[condition].cells.helpful.delaunay = delaunay_helpful;

            D[condition].coocurence_matrix = D[condition].matrix[0].map((row) => row.slice());
            for (let i = 1; i < D[condition].matrix.length; ++i) {
                const Mi = D[condition].matrix[i];
                for (let n = 0; n < all_names.length; ++n) {
                    for (let m = 0; m < all_names.length; ++m) {
                        D[condition].coocurence_matrix[n][m] += Mi[n][m];
                    }
                }
            }
            //D[condition].
            data.push([condition, D[condition]]);
            data_dict[condition] = data.length - 1;
        }
        labels = Array.from(new Set(data[0][1].labels));
        filter = data[0][1].index.map(() => true);
        data = data.slice();
        console.log(data);
        loading = false;
    }

    let filter = null;
    $: coocurence_matrices = compute_coocurence_matrices(data, filter);
    $: coocurence_matrix_colorScale = d3.scaleSequential(d3.interpolatePurples).domain([0, d3.max(coocurence_matrices.flat().flat(), (d) => d)]);
    $: coocurence_matrix_colorScale_relative = d3.scaleSequential(d3.interpolateOranges);
    $: legend({ svg: coocurence_svg, color: coocurence_matrix_colorScale, width: 224, marginLeft: 5 });
    $: if (relative_coocurence == "relative") {
        legend({ svg: coocurence_svg_relative, color: coocurence_matrix_colorScale_relative, width: 224, marginLeft: 5 });
    }
    let coocurence_svg_relative;

    function compute_coocurence_matrices(data, filter) {
        return data.map(([condition, D]) => {
            const coocurence_matrix = D.matrix[0].map((row) => row.slice());
            for (let i = 1; i < D.matrix.length; ++i) {
                const Mi = D.matrix[i];
                if (filter == null || filter[i] == true) {
                    for (let n = 0; n < all_names.length; ++n) {
                        for (let m = 0; m < all_names.length; ++m) {
                            coocurence_matrix[n][m] += Mi[n][m];
                        }
                    }
                }
            }
            return coocurence_matrix;
        });
    }

    let coocurence_matrix_colorScale = null;
    let coocurence_svg;

    let tooltip;
    let highlight = null;
    let extend = false;
    let strg = false;
    let highlight_info = null;
    //$: console.log(highlight_info)

    function ramp(color, n = 256) {
        const canvas = d3.create("canvas").attr("width", n).attr("height", 1).node();
        const context = canvas.getContext("2d");
        for (let i = 0; i < n; ++i) {
            context.fillStyle = color(i / (n - 1));
            context.fillRect(i, 0, 1, 1);
        }
        return canvas;
    }

    function legend({ svg, color, title, tickSize = 6, width = 240, height = 44 + tickSize, marginTop = 18, marginRight = 0, marginBottom = 16 + tickSize, marginLeft = 0, ticks = width / 64, tickFormat, tickValues } = {}) {
        svg = d3.select(svg).attr("width", width).attr("height", height).attr("viewBox", [0, 0, width, height]).style("overflow", "visible").style("display", "block");

        svg.selectAll("*").remove();

        let tickAdjust = (g) => g.selectAll(".tick line").attr("y1", marginTop + marginBottom - height);
        let x;

        // Continuous
        if (color.interpolate) {
            const n = Math.min(color.domain().length, color.range().length);

            x = color.copy().rangeRound(d3.quantize(d3.interpolate(marginLeft, width - marginRight), n));
            svg.append("image")
                .attr("x", marginLeft)
                .attr("y", marginTop)
                .attr("width", width - marginLeft - marginRight)
                .attr("height", height - marginTop - marginBottom)
                .attr("preserveAspectRatio", "none")
                .attr("xlink:href", ramp(color.copy().domain(d3.quantize(d3.interpolate(0, 1), n))).toDataURL());
        }

        // Sequential
        else if (color.interpolator) {
            x = Object.assign(color.copy().interpolator(d3.interpolateRound(marginLeft, width - marginRight)), {
                range() {
                    return [marginLeft, width - marginRight];
                },
            });

            svg.append("image")
                .attr("x", marginLeft)
                .attr("y", marginTop)
                .attr("width", width - marginLeft - marginRight)
                .attr("height", height - marginTop - marginBottom)
                .attr("preserveAspectRatio", "none")
                .attr("xlink:href", ramp(color.interpolator()).toDataURL());

            // scaleSequentialQuantile doesn’t implement ticks or tickFormat.
            if (!x.ticks) {
                if (tickValues === undefined) {
                    const n = Math.round(ticks + 1);
                    tickValues = d3.range(n).map((i) => d3.quantile(color.domain(), i / (n - 1)));
                }
                if (typeof tickFormat !== "function") {
                    tickFormat = d3.format(tickFormat === undefined ? ",f" : tickFormat);
                }
            }
        }

        // Threshold
        else if (color.invertExtent) {
            const thresholds = color.thresholds
                ? color.thresholds() // scaleQuantize
                : color.quantiles
                ? color.quantiles() // scaleQuantile
                : color.domain(); // scaleThreshold

            const thresholdFormat = tickFormat === undefined ? (d) => d : typeof tickFormat === "string" ? d3.format(tickFormat) : tickFormat;

            x = d3
                .scaleLinear()
                .domain([-1, color.range().length - 1])
                .rangeRound([marginLeft, width - marginRight]);

            svg.append("g")
                .selectAll("rect")
                .data(color.range())
                .join("rect")
                .attr("x", (d, i) => x(i - 1))
                .attr("y", marginTop)
                .attr("width", (d, i) => x(i) - x(i - 1))
                .attr("height", height - marginTop - marginBottom)
                .attr("fill", (d) => d);

            tickValues = d3.range(thresholds.length);
            tickFormat = (i) => thresholdFormat(thresholds[i], i);
        }

        // Ordinal
        else {
            x = d3
                .scaleBand()
                .domain(color.domain())
                .rangeRound([marginLeft, width - marginRight]);

            svg.append("g")
                .selectAll("rect")
                .data(color.domain())
                .join("rect")
                .attr("x", x)
                .attr("y", marginTop)
                .attr("width", Math.max(0, x.bandwidth() - 1))
                .attr("height", height - marginTop - marginBottom)
                .attr("fill", color);

            tickAdjust = () => {};
        }

        svg.append("g")
            .attr("transform", `translate(0,${height - marginBottom})`)
            .call(
                d3
                    .axisBottom(x)
                    .ticks(ticks, typeof tickFormat === "string" ? tickFormat : undefined)
                    .tickFormat(typeof tickFormat === "function" ? tickFormat : undefined)
                    .tickSize(tickSize)
                    .tickValues(tickValues)
            )
            .call(tickAdjust)
            .call((g) => g.select(".domain").remove())
            .call((g) =>
                g
                    .append("text")
                    .attr("x", marginLeft)
                    .attr("y", marginTop + marginBottom - height - 6)
                    .attr("fill", "currentColor")
                    .attr("text-anchor", "start")
                    .attr("font-weight", "bold")
                    .attr("class", "title")
                    .text(title)
            );

        return svg.node();
    }

    let r = 0;
    let mouse = { x: 0, y: 0 };

    let relative_coocurence = "absolute";

    function filename(i, condition, papertype, suffix = "_thumb") {
        let F = data[0][1].index[i];
        const year = F.slice(0, 4);
        const file = F.slice(5, -4);
        const file_end = F.slice(-3);
        if (year == "2019") {
            const number_re = /\d+/;
            const number = number_re.exec(file)[0];
            //if (isNaN(number)) console.log(file, number, `./data/all_images/${year}_300/thumb/${papertype}_${condition}_${number}_thumb.png`)
            return `${window.location.href}data/all_images/${year}_300/thumb/${papertype}_${condition}_${number}${suffix}.png`;
        } else {
            return `${window.location.href}data/all_images/${year}_300/thumb/${file}_${condition}${suffix}.png`;
        }
    }

    let showLegend = true;
    let showSettings = true;
</script>

<MaterialAppMin theme="light">
    <slot style="position: fixed" />
    <NavigationDrawer fixed class="" clipped={true}>
        <List>
            <ListGroup bind:active={showLegend} class="">
                <span slot="prepend"><Icon path={mdiMapLegend} /></span>
                <span slot="activator">Legend</span>
                <span slot="append"><Icon path={mdiChevronUp} rotate={showLegend ? 0 : 180} /></span>

                <Subheader>Datapoints</Subheader>
                <ListItemGroup>
                    {#each labels as label, i}
                        <ListItem dense>
                            <span slot="prepend">
                                <svg width="1em" height="1em">
                                    <g transform="translate(5, 10)">
                                        <path d={shape_scale(label)} fill={colorBy == "conference" ? label_scale(label) : ranking_scale(1)} />
                                    </g>
                                </svg>
                            </span>
                            {label}
                        </ListItem>
                    {/each}
                </ListItemGroup>
                <Subheader>Co-Occurrence</Subheader>
                <Row class="pl-2 pr-2">
                    <Col>
                        <Select
                            dense
                            items={["absolute", "relative"].map((k) => {
                                return { name: k, value: k };
                            })}
                            bind:value={relative_coocurence}
                        >
                            values
                        </Select>
                    </Col>
                </Row>
                <ListItemGroup>
                    <ListItem selectable="false" dense>
                        <svg width="400" height="50" bind:this={coocurence_svg} />
                    </ListItem>
                    {#if relative_coocurence == "relative"}
                        <ListItem dense>
                            <svg width="400" height="50" bind:this={coocurence_svg_relative} />
                        </ListItem>
                    {/if}
                </ListItemGroup>
                <!-- <ListItemGroup>
                <ListItem dense>
                    <Radio bind:group={relative_coocurence} value={"absolute"}>Absolute</Radio>
                </ListItem>
                <ListItem dense>
                    <Radio bind:group={relative_coocurence} value={"relative"}>Relative</Radio>
                </ListItem>
            </ListItemGroup> -->
            </ListGroup>
            <ListGroup bind:active={showSettings} class="">
                <span slot="prepend"><Icon path={mdiCog} /></span>
                <span slot="activator">Settings</span>
                <span slot="append"><Icon path={mdiChevronUp} rotate={showSettings ? 0 : 180} /></span>
                <!-- <Subheader>Color by</Subheader> -->
                <Row class="pl-2 pr-2">
                    <Col>
                        <Select dense items={aggregations} bind:value={aggregation}>Aggregation by</Select>
                    </Col>
                </Row>
                <Row class="pl-2 pr-2">
                    <Col>
                        <Select
                            dense
                            class=""
                            items={["conference", ...rankings].map((k) => {
                                return { name: k.slice(0, 1).toUpperCase() + k.slice(1), value: k };
                            })}
                            bind:value={colorBy}
                        >
                            Color By
                        </Select>
                    </Col>
                </Row>
                <!-- <ListItemGroup>
                <ListItem dense>
                    <Radio bind:group={colorBy} value={"conference"}>Conference</Radio>
                </ListItem>
                {#each rankings as ranking}
                    <ListItem dense>
                        <Radio bind:group={colorBy} value={ranking}>{ranking.replace("_", " ")}</Radio>
                    </ListItem>
                {/each}
            </ListItemGroup> -->

                <!-- <Subheader>VisType</Subheader>
            <ListItemGroup>
                <ListItem dense>
                    <Radio bind:group={showWhat} value={"points"}>Points</Radio>
                </ListItem>
                <ListItem dense>
                    <Radio bind:group={showWhat} value={"images"}>Images</Radio>
                </ListItem>
            </ListItemGroup> -->
                <Row class="pl-2 pr-2">
                    <Col>
                        <Select
                            dense
                            items={["points", "images"].map((k) => {
                                return { name: k.slice(0, 1).toUpperCase() + k.slice(1), value: k };
                            })}
                            bind:value={showWhat}
                        >
                            Markings
                        </Select>
                    </Col>
                </Row>
                <Row class="pl-2 pr-2">
                    <Col>
                        <TextField dense bind:value={r} placeholder="Radius">Brush Radius</TextField>
                    </Col>
                </Row>
                <Subheader>Tooltip</Subheader>
                <Row class="pl-2 pr-2">
                    <Col>
                        <Switch bind:checked={extend} style="">Extended</Switch>
                        <Switch bind:checked={strg} style="">Co-occurence matrix</Switch>
                    </Col>
                </Row>
            </ListGroup>
        </List>
    </NavigationDrawer>
    {#if loading == true}
        <Overlay {loading}><ProgressCircular color="white" indeterminate size={128} /></Overlay>
    {:else}
        <Container fluid style="margin-left: 256px; width: calc(100vw - 256px); display: grid; grid-template-columns: 2em {width+24}px {width+24}px {width+75+24}px; gap: 1em;">
            <Col style="" />
            <Col style="text-align: center;">Issues</Col>
            <Col style="text-align: center;">Helpful</Col>
            <Col style="text-align: center;">Co-Occurence</Col>
            {#each data as [condition, D], ci}
                <Col style="writing-mode: vertical-lr; text-align: center;">{condition}</Col>
                {#each ["issues", "helpful"] as aspect}
                    <Col class="p-2 elevation-3">
                        <svg
                            {width}
                            height={width}
                            on:mousewheel={(event) => {
                                if (event.shiftKey) {
                                    event.preventDefault();
                                    r = Math.round(Math.max(0, r - event.deltaY / 10));
                                    let [px, py] = D.projection[aspect][highlight];
                                    px = D[aspect].x(px);
                                    py = D[aspect].y(py);
                                    let distances = D.projection[aspect].map(([ox, oy]) => {
                                        ox = D[aspect].x(ox);
                                        oy = D[aspect].y(oy);
                                        return Math.hypot(px - ox, py - oy);
                                    });
                                    //console.log(r, distances)
                                    filter = distances.map((d) => d < r);
                                }
                            }}
                            on:mousemove={(event) => {
                                //console.log(event)
                                mouse.x = event.offsetX;
                                mouse.y = event.offsetY;

                                highlight_info = {
                                    condition: condition,
                                    aspect: aspect,
                                };
                                highlight = D.cells[aspect].delaunay.find(event.offsetX, event.offsetY);
                                if (r == 0) {
                                    /* extend = extend && event.shiftKey
                            strg = strg && event.ctrlKey; */
                                    //console.log(data[0][1].index[highlight].slice(0, 4), data[0][1].index[highlight].slice(5, -4), data[0][1].index[highlight].slice(-3))
                                    let w = width;
                                    if (extend) w += width * 2;
                                    if (strg) w += width;
                                    d3.select(tooltip)
                                        .style("top", event.clientY > window.innerHeight / 2 ? null : event.pageY + 15 + "px")
                                        .style("bottom", event.clientY > window.innerHeight / 2 ? window.innerHeight - event.pageY + 15 + "px" : null)
                                        .style("left", Math.min(Math.max(0, event.clientX - w / 2), window.innerWidth - w) + "px");
                                } else {
                                    let [px, py] = D.projection[aspect][highlight];
                                    px = D[aspect].x(px);
                                    py = D[aspect].y(py);
                                    let distances = D.projection[aspect].map(([ox, oy]) => {
                                        ox = D[aspect].x(ox);
                                        oy = D[aspect].y(oy);
                                        return Math.hypot(px - ox, py - oy);
                                    });
                                    //console.log(r, distances)
                                    filter = distances.map((d) => d < r);
                                }
                            }}
                            on:mouseleave={() => {
                                highlight = null;
                                highlight_info = null;
                                filter = null;
                            }}
                        >
                            {#each D.cells[aspect] as cell, i}
                                <path d="M {cell.join(', ')} Z" fill="white" />
                            {/each}
                            {#each D.projection[aspect] as [px, py], i}
                                <g transform="translate({D[aspect].x(px)}, {D[aspect].y(py)})">
                                    {#if showWhat == "points"}
                                        <path d={shape_scale(D.labels[i])} fill={highlight == i ? (colorBy == "conference" ? label_scale(D.labels[i]) : ranking_scale(D.ranking[i][rankings_index[colorBy]])) : "none"} stroke={colorBy == "conference" ? label_scale(D.labels[i]) : ranking_scale(D.ranking[i][rankings_index[colorBy]])} opacity={(r > 0 && filter && filter[i]) || highlight ? ((r > 0 && filter && filter[i]) || highlight == i ? 1 : 0.15) : 1} pointer-events="none" />
                                    {:else if showWhat == "images"}
                                        <image width="20" height="20" xlink:href={filename(i, condition, D.labels[i])} />
                                    {/if}
                                </g>
                            {/each}
                            {#if highlight_info && highlight_info.condition == condition && highlight_info.aspect == aspect && r > 0}
                                <circle cx={D[aspect].x(D.projection[aspect][highlight][0])} cy={D[aspect].y(D.projection[aspect][highlight][1])} {r} fill="none" stroke="black" />
                            {/if}
                        </svg>
                    </Col>
                {/each}
                <Col class="elevation-3 p-2">
                    <svg width={width + 75} height={width}>
                        {#each all_names as li, i}
                            <!-- <g transform="translate({i * Math.floor((width - 5) /all_names.length) + 1 + (i > issues_names.length ? 5 : 0)}, {i * Math.floor((width - 5) /all_names.length) + 1 + (i > issues_names.length ? 5 : 0)})">
                        <g transform="translate({Math.floor((width - 5) /all_names.length) / 2}, {Math.floor((width - 5) /all_names.length) + 5}) rotate(-90)">
                            <text text-anchor="end" dominant-baseline="central">{li}</text>
                        </g>
                    </g> -->
                            <g transform="translate({width}, {i * Math.floor((width - 5) / all_names.length) + 1 + (i > issues_names.length ? 5 : 0) + Math.floor((width - 5) / all_names.length) / 2})">
                                <text dominant-baseline="central">{names_dict[li]}</text>
                            </g>
                            {#each all_names as lj, j}
                                {#if j <= i}
                                    <rect x={i * Math.floor((width - 5) / all_names.length) + 1 + (i > issues_names.length ? 5 : 0)} y={j * Math.floor((width - 5) / all_names.length) + 1 + (j > issues_names.length ? 5 : 0)} width={Math.floor((width - 5) / all_names.length) - 2} height={Math.floor((width - 5) / all_names.length) - 2} fill={j < i && relative_coocurence == "relative" ? coocurence_matrix_colorScale_relative(coocurence_matrices[ci][i][j] / coocurence_matrices[ci][i][i]) : coocurence_matrix_colorScale(coocurence_matrices[ci][i][j])}>
                                        <title>
                                            {names_dict[li]} & {names_dict[lj]}{"\n"}
                                            {#if j < i && relative_coocurence == "relative"}
                                                {d3.format(".2f")((coocurence_matrices[ci][i][j] / coocurence_matrices[ci][i][i]) * 100)}%
                                            {:else}
                                                {coocurence_matrices[ci][i][j]} of {coocurence_matrices[ci][i][i]}
                                            {/if}
                                        </title>
                                    </rect>
                                    <!-- <text 
                            x={i * Math.floor((width - 5) /all_names.length) + Math.floor((width - 5) /all_names.length) / 2 + (i > issues_names.length ? 5 : 0)}
                            y={j * Math.floor((width - 5) /all_names.length) + Math.floor((width - 5) /all_names.length) / 2 + (j > issues_names.length ? 5 : 0)}
                            dominant-baseline="central"
                            text-anchor="middle"
                            fill="black">
                            {D.coocurence_matrix[i][j]}
                        </text> -->
                                {/if}
                            {/each}
                        {/each}
                    </svg>
                </Col>
            {/each}

            <div id="tooltip" class="theme--dark" bind:this={tooltip} style="visibility: {highlight && r == 0 ? 'visible' : 'hidden'}; border-left: 1em solid {highlight ? label_scale(data[data_dict[highlight_info.condition]][1].labels[highlight]) : null}">
                {#if highlight}
                    <div style="width: {width}px; padding-right: 1em;">
                        <Subheader>{data[data_dict[highlight_info.condition]][1].index[highlight]}</Subheader>
                        {#if extend}
                            <Subheader>RANKINGS</Subheader>
                            <svg {width} height="30" shape-rendering="crispEdges">
                                {#each d3.zip(rankings, data[data_dict[highlight_info.condition]][1].ranking[highlight]) as [title, value], i}
                                    <!-- <g transform="translate(10, {i * 100/rankings.length})">
                                <text fill="white" y={100/rankings.length / 2} dominant-baseline="central">{title}</text>
                                <rect x=100 y=4 height={100/rankings.length / 2 - 8} width={350 * value} fill={d3.scaleSequential(rankings_color_scale[title])(value)}>
                            </g>     -->
                                    <g transform="translate({data[data_dict[highlight_info.condition]][1].ranking[highlight].slice(0, i).reduce((a, b) => a + b, 0) * width}, 0)">
                                        <rect width={width * value} height="30" fill={d3.scaleSequential(rankings_color_scale[title])(1)} />
                                        {#if value != 0}
                                            <text fill="white" x={(width / 2) * value} y="15" dominant-baseline="central" text-anchor="middle">
                                                {d3.format(".2f")(value * 100)}%
                                            </text>
                                        {/if}
                                    </g>
                                {/each}
                            </svg>
                            <br />
                        {/if}
                        <div style="display: flex; width: {width}px; gap: 1rem;">
                            <div style="overflow: ellipsis; display: inline-block;">
                                <img width="{width / 2}px" max-height="100px" src="{window.location.href}data/all_images/{data[0][1].index[highlight].slice(0, 4)}_300/Original/{data[0][1].index[highlight].slice(5)}" alt="{window.location.href}data/all_images/{data[0][1].index[highlight].slice(0, 4)}_300/Original/{data[0][1].index[highlight].slice(5)}" />
                            </div>
                            <div style="overflow: hidden; display: inline-block;">
                                <img width="{width / 2}px" max-height="100px" src="{window.location.href}data/all_images/{data[0][1].index[highlight].slice(0, 4)}_300/outputImages/{aggregation == 'cvd' ? highlight_info.condition : data[data_dict[highlight_info.condition]][1].labels[highlight]}/{data[0][1].index[highlight].slice(5, -4)}_{aggregation == 'cvd' ? highlight_info.condition : data[data_dict[highlight_info.condition]][1].labels[highlight]}.png" alt="{window.location.href}data/all_images/{data[0][1].index[highlight].slice(0, 4)}_300/outputImages/{highlight_info.condition}/{data[0][1].index[highlight].slice(5, -4)}_{highlight_info.condition}.png" />
                            </div>
                        </div>
                    </div>
                    {#if strg}
                        <div>
                            <Subheader>CO-OCCURENCE MATRIX</Subheader>
                            <br />
                            <svg {width} height={width}>
                                {#each all_names as li, i}
                                    {#each all_names as lj, j}
                                        {#if j <= i}
                                            <rect x={i * Math.floor((width - 5) / all_names.length) + 1 + (i > issues_names.length ? 5 : 0)} y={j * Math.floor((width - 5) / all_names.length) + 1 + (j > issues_names.length ? 5 : 0)} width={Math.floor((width - 5) / all_names.length) - 2} height={Math.floor((width - 5) / all_names.length) - 2} fill={d3.scaleSequential(d3.interpolateBlues).domain([0, 5])(data[data_dict[highlight_info.condition]][1].matrix[highlight][i][j])} />
                                            <text x={i * Math.floor((width - 5) / all_names.length) + Math.floor((width - 5) / all_names.length) / 2 + (i > issues_names.length ? 5 : 0)} y={j * Math.floor((width - 5) / all_names.length) + Math.floor((width - 5) / all_names.length) / 2 + (j > issues_names.length ? 5 : 0)} dominant-baseline="central" text-anchor="middle" fill="black">
                                                {data[data_dict[highlight_info.condition]][1].matrix[highlight][i][j]}
                                            </text>
                                        {/if}
                                    {/each}
                                {/each}
                            </svg>
                        </div>
                    {/if}
                    {#if extend}
                        <div>
                            <Subheader>ISSUES</Subheader>
                            <br />
                            <svg {width} height="400">
                                {#each d3.zip(issues_names, data[data_dict[highlight_info.condition]][1].values.issues[highlight]) as [title, value], i}
                                    <g transform="translate({(i * width) / issues_names.length}, 0)">
                                        <rect y={100 - value * 100} height={value * 100} fill="#661100" x="4" width={width / issues_names.length - 8} />
                                        <text fill="white" x={width / 2 / issues_names.length} y={100 - value * 100 - 5} text-anchor="middle">{value * 100}%</text>
                                        <g transform="translate({width / 2 / issues_names.length}, 110) rotate(90)">
                                            <text fill="white">{title}</text>
                                        </g>
                                    </g>
                                {/each}
                            </svg>
                        </div>
                        <div>
                            <Subheader>HELPFUL</Subheader>
                            <br />
                            <svg {width} height="400">
                                {#each d3.zip(helpful_names, data[data_dict[highlight_info.condition]][1].values.helpful[highlight]) as [title, value], i}
                                    <g transform="translate({(i * width) / helpful_names.length}, 0)">
                                        <rect y={100 - value * 100} height={value * 100} fill="#117733" x="4" width={width / helpful_names.length - 8} />
                                        <text fill="white" x={width / 2 / helpful_names.length} y={100 - value * 100 - 5} text-anchor="middle">{value * 100}%</text>
                                        <g transform="translate({width / 2 / helpful_names.length}, 110) rotate(90)">
                                            <text fill="white">{title}</text>
                                        </g>
                                    </g>
                                {/each}
                            </svg>
                        </div>
                    {/if}
                {/if}
            </div>
        </Container>
    {/if}
</MaterialAppMin>

<style>
    #tooltip {
        font-size: 0.8rem;
        display: flex;
        position: absolute;
        pointer-events: none;
        padding: 1rem;
        background-color: rgba(0, 0, 0, 0.5);
        color: white;
        box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
        border-radius: 0.5rem;
        z-index: 9;
    }
</style>
